import gql from 'graphql-tag';

import {Graph, Project, View} from '../types/graphql';
import {AlertSubscription} from '../generated/graphql';

export const fragments = {
  view: gql`
    fragment ViewFragment on View {
      id
      type
      name
      displayName
      description
      user {
        id
        username
        photoUrl
        admin
      }
      project {
        id
        name
        entityName
      }
      spec
      updatedAt
    }
  `,
  viewMetadata: gql`
    fragment ViewFragmentMetadata on View {
      id
      type
      name
      displayName
      description
      updatedBy {
        id
        username
      }
      user {
        id
        username
        photoUrl
        admin
      }
      project {
        id
        name
        entityName
      }
      updatedAt
      createdAt
      starred
      starCount
      parentId
      locked
      viewCount
      alertSubscription {
        id
      }
    }
  `,
  // benchmarkRun: gql`
  //   fragment BenchmarkRunFragment on BenchmarkRun {
  //     id
  //     user {
  //       id
  //       username
  //       photoUrl
  //       admin
  //     }
  //     details
  //     benchmark {
  //       name
  //     }
  //   }
  // `,
};

export interface ViewMetadata {
  id: string;
  name: string;
  displayName: string;
  description: string;
  updatedBy?: {
    id: string;
    username: string;
  };
  user: {
    id: string;
    username: string;
    photoUrl: string;
    admin: boolean;
  };
  project: {
    id: string;
    name: string;
  };
  spec: string;
  createdAt: string;
  updatedAt: string;
  starCount: number;
  starred: boolean;
  parentId: string;
  locked: boolean;
  alertSubscription: AlertSubscription;
}

export interface ViewResult {
  project: Project;
}

export interface ViewQueryResult {
  project: {
    id: string;
    totalRunsWithGroup: number;
    totalRunsWithJobType: number;
    allViews: Graph<View>;
    projectSettings: Graph<View>;
  };
}

export interface ViewUpsertResult {
  inserted: boolean;
  view: View;
  upsertView: {
    view: View;
  };
}

export const VIEW_QUERY = gql`
  query Views(
    $entityName: String
    $name: String
    $viewType: String = "runs"
    $userName: String
    $viewName: String
  ) {
    project(name: $name, entityName: $entityName) {
      id
      views
      totalRunsWithGroup
      totalRunsWithJobType
      allViews(viewType: $viewType, viewName: $viewName, userName: $userName) {
        edges {
          node {
            ...ViewFragment
          }
        }
      }
      projectSettings: allViews(
        viewType: "project-settings"
        viewName: "project-settings"
        first: 1
      ) {
        edges {
          node {
            ...ViewFragment
          }
        }
      }
    }
  }
  ${fragments.view}
`;

// Just the views metadata. This code is going to change with project Rubik
export const VIEW_QUERY_METADATA = gql`
  query ViewsMetadata(
    $entityName: String
    $name: String
    $viewType: String = "runs"
    $userName: String
    $viewName: String
  ) {
    project(name: $name, entityName: $entityName) {
      id
      views
      totalRunsWithGroup
      totalRunsWithJobType
      allViews(viewType: $viewType, viewName: $viewName, userName: $userName) {
        edges {
          node {
            ...ViewFragmentMetadata
          }
        }
      }
    }
  }
  ${fragments.viewMetadata}
`;

export const VIEW_UPSERT = gql`
  mutation upsertView(
    $id: ID
    $entityName: String
    $projectName: String
    $type: String
    $name: String
    $description: String
    $spec: String!
  ) {
    upsertView(
      input: {
        id: $id
        entityName: $entityName
        projectName: $projectName
        name: $name
        description: $description
        type: $type
        spec: $spec
      }
    ) {
      view {
        ...ViewFragment
      }
      inserted
    }
  }
  ${fragments.view}
`;

export const VIEW_DELETE = gql`
  mutation deleteView($id: ID) {
    deleteView(input: {id: $id}) {
      success
    }
  }
`;

export const VIEW_CREATE_SHARED = gql`
  mutation createSharedView(
    $entityName: String!
    $name: String!
    $type: String!
    $description: String
    $spec: String!
  ) {
    upsertSharedView(
      input: {
        entityName: $entityName
        name: $name
        type: $type
        description: $description
        spec: $spec
      }
    ) {
      view {
        ...ViewFragment
      }
    }
  }
  ${fragments.view}
`;
